// src/store/slices/draftSlice.ts
/**
 * Draft State Management (T-23)
 * 
 * Redux slice for managing local persistence state and operations.
 * Tracks draft availability, save status, and user preferences.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  saveDraftToStorage, 
  loadDraftFromStorage, 
  clearDraftFromStorage,
  isDraftAvailable,
  getDraftMetadata,
  type DraftMetadata,
  type SaveDraftResult,
  type LoadDraftResult 
} from '../../lib/localPersistence';
import type { RootState } from '../../types/redux';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface DraftState {
  /** Timestamp of last successful save */
  lastSaved: string | null;
  
  /** Whether auto-save is currently enabled (mirrors preferences.autoSave) */
  autoSaveEnabled: boolean;
  
  /** Whether a draft is available in localStorage */
  isDraftAvailable: boolean;
  
  /** Whether the current state was loaded from a draft */
  loadedFromDraft: boolean;
  
  /** Metadata about the current draft */
  draftMetadata: DraftMetadata | null;
  
  /** Last save operation result */
  lastSaveResult: SaveDraftResult | null;
  
  /** Last load operation result */
  lastLoadResult: LoadDraftResult | null;
  
  /** Current error message, if any */
  lastError: string | null;
  
  /** Whether a save operation is currently in progress */
  isSaving: boolean;
  
  /** Whether a load operation is currently in progress */
  isLoading: boolean;
  
  /** Whether to show the draft restoration banner */
  showRestorationBanner: boolean;
  
  /** Whether to temporarily suppress auto-save (for start fresh operations) */
  suppressAutoSave: boolean;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: DraftState = {
  lastSaved: null,
  autoSaveEnabled: true, // Will be synced with preferences
  isDraftAvailable: false,
  loadedFromDraft: false,
  draftMetadata: null,
  lastSaveResult: null,
  lastLoadResult: null,
  lastError: null,
  isSaving: false,
  isLoading: false,
  showRestorationBanner: false,
  suppressAutoSave: false,
};

// =============================================================================
// REDUX SLICE
// =============================================================================

export const draftSlice = createSlice({
  name: 'draft',
  initialState,
  reducers: {
    /**
     * Initialize draft state on app startup
     */
    initializeDraftState: (state) => {
      state.isDraftAvailable = isDraftAvailable();
      state.draftMetadata = getDraftMetadata();
      state.lastError = null;
    },

    /**
     * Set auto-save enabled status (synced from preferences)
     */
    setAutoSaveEnabled: (state, action: PayloadAction<boolean>) => {
      console.log('🔧 Setting auto-save enabled:', action.payload);
      state.autoSaveEnabled = action.payload;
      
      // Clear error if auto-save is being disabled
      if (!action.payload) {
        state.lastError = null;
      }
    },

    /**
     * Start a manual save operation
     */
    startSave: (state) => {
      state.isSaving = true;
      state.lastError = null;
    },

    /**
     * Complete a save operation with result
     */
    completeSave: (state, action: PayloadAction<SaveDraftResult>) => {
      state.isSaving = false;
      state.lastSaveResult = action.payload;

      if (action.payload.success) {
        state.lastSaved = new Date().toISOString();
        state.isDraftAvailable = true;
        state.draftMetadata = getDraftMetadata();
        state.lastError = null;
      } else {
        state.lastError = action.payload.error || 'Save failed';
      }
    },

    /**
     * Start a load operation
     */
    startLoad: (state) => {
      state.isLoading = true;
      state.lastError = null;
    },

    /**
     * Complete a load operation with result
     */
    completeLoad: (state, action: PayloadAction<LoadDraftResult>) => {
      state.isLoading = false;
      state.lastLoadResult = action.payload;

      if (action.payload.success) {
        state.loadedFromDraft = true;
        state.showRestorationBanner = true;
        state.lastError = null;
      } else {
        state.lastError = action.payload.error || 'Load failed';
      }
    },

    /**
     * Clear the stored draft
     */
    clearDraft: (state) => {
      const success = clearDraftFromStorage();
      
      if (success) {
        state.isDraftAvailable = false;
        state.draftMetadata = null;
        state.lastSaved = null;
        state.lastSaveResult = null;
        state.lastLoadResult = null;
        state.loadedFromDraft = false;
        state.showRestorationBanner = false;
        state.lastError = null;
      } else {
        state.lastError = 'Failed to clear draft from storage';
      }
    },

    /**
     * Mark current state as loaded from draft
     */
    setLoadedFromDraft: (state, action: PayloadAction<boolean>) => {
      state.loadedFromDraft = action.payload;
      
      if (action.payload) {
        state.showRestorationBanner = true;
      }
    },

    /**
     * Dismiss the restoration banner
     */
    dismissRestorationBanner: (state) => {
      state.showRestorationBanner = false;
    },

    /**
     * Set error message
     */
    setDraftError: (state, action: PayloadAction<string | null>) => {
      state.lastError = action.payload;
    },

    /**
     * Clear all error messages
     */
    clearDraftErrors: (state) => {
      state.lastError = null;
    },

    /**
     * Update draft metadata (called after successful saves)
     */
    updateDraftMetadata: (state) => {
      state.draftMetadata = getDraftMetadata();
      state.isDraftAvailable = isDraftAvailable();
    },

    /**
     * Reset draft state (for testing or manual reset)
     */
    resetDraftState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Suppress auto-save temporarily (for start fresh operations)
     */
    suppressAutoSave: (state, action: PayloadAction<boolean>) => {
      state.suppressAutoSave = action.payload;
    },
  },
});

// =============================================================================
// ACTIONS
// =============================================================================

export const {
  initializeDraftState,
  setAutoSaveEnabled,
  startSave,
  completeSave,
  startLoad,
  completeLoad,
  clearDraft,
  setLoadedFromDraft,
  dismissRestorationBanner,
  setDraftError,
  clearDraftErrors,
  updateDraftMetadata,
  resetDraftState,
  suppressAutoSave,
} = draftSlice.actions;

// =============================================================================
// THUNK ACTIONS (Async Operations)
// =============================================================================

/**
 * Async thunk to manually save current state as draft
 */
export const saveDraft = () => async (dispatch: any, getState: () => RootState) => {
  dispatch(startSave());
  
  try {
    const state = getState();
    const result = await saveDraftToStorage(state);
    dispatch(completeSave(result));
    
    return result;
  } catch (error) {
    const errorResult: SaveDraftResult = {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during save'
    };
    dispatch(completeSave(errorResult));
    return errorResult;
  }
};

/**
 * Async thunk to load draft and restore state
 */
export const loadDraft = () => async (dispatch: any, getState: () => RootState) => {
  dispatch(startLoad());
  
  try {
    const result = loadDraftFromStorage();
    dispatch(completeLoad(result));
    
    if (result.success && result.data) {
      // The actual state restoration will be handled by the middleware
      // This just tracks the load operation
      return result;
    }
    
    return result;
  } catch (error) {
    const errorResult: LoadDraftResult = {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during load'
    };
    dispatch(completeLoad(errorResult));
    return errorResult;
  }
};

/**
 * Clear draft and start fresh
 */
export const startFresh = () => (dispatch: any) => {
  dispatch(clearDraft());
  dispatch(setLoadedFromDraft(false));
};

// =============================================================================
// SELECTORS
// =============================================================================

export const selectDraftState = (state: { draft: DraftState }) => state.draft;

export const selectIsDraftAvailable = (state: { draft: DraftState }) => 
  state.draft.isDraftAvailable;

export const selectLoadedFromDraft = (state: { draft: DraftState }) => 
  state.draft.loadedFromDraft;

export const selectShowRestorationBanner = (state: { draft: DraftState }) => 
  state.draft.showRestorationBanner;

export const selectDraftMetadata = (state: { draft: DraftState }) => 
  state.draft.draftMetadata;

export const selectLastSaved = (state: { draft: DraftState }) => 
  state.draft.lastSaved;

export const selectDraftError = (state: { draft: DraftState }) => 
  state.draft.lastError;

export const selectAutoSaveEnabled = (state: { draft: DraftState }) => 
  state.draft.autoSaveEnabled;

export const selectIsSaving = (state: { draft: DraftState }) => 
  state.draft.isSaving;

export const selectIsLoading = (state: { draft: DraftState }) => 
  state.draft.isLoading;

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default draftSlice.reducer;